import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type PostCloseItem } from '../lib/api';
import { pokePostClose } from '../components/PostCloseAlerts';

/**
 * 마감 후 수정 — 팀 승인(2026-10-28 way).
 *
 * DYOPS 「마감 후 수정」 기안이 제출되면 여기 올라온다. 관리기사A·직영사업장 기사 줄이면 회계팀 + 인차팀,
 * 그 밖(관리기사B·외부·거래처 청구액)은 회계팀이 승인한다. 필요한 팀이 **다** 승인하면 버스인웹에 바로 반영된다.
 * DYOPS 결재(1차 팀장 · 2차 대표)는 근거로 남을 뿐 반영을 일으키지 않는다. 판정·강제는 서버(`postCloseReview.ts`).
 */

const STATUS: Record<PostCloseItem['status'], { label: string; tone: string }> = {
  review: { label: '승인 대기', tone: 'bg-amber-100 text-amber-800' },
  applying: { label: '반영 중', tone: 'bg-sky-100 text-sky-800' },
  applied: { label: '반영 완료', tone: 'bg-emerald-100 text-emerald-800' },
  held: { label: '멈춤', tone: 'bg-red-100 text-red-700' },
  failed: { label: '반영 실패', tone: 'bg-red-100 text-red-700' },
  rejected: { label: '팀 반려', tone: 'bg-red-100 text-red-700' },
  cancelled: { label: 'DYOPS 에서 거둠', tone: 'bg-canvas text-ink-soft' },
};
const TYPE_LABEL: Record<string, string> = {
  cust: '거래처 청구액', driver_cost: '기사 운행료 단가', run_cnt: '통근 운행횟수',
  req: '영업정산', daily: '재무추가', mo: '월정기', line: '통근 기사 줄',
};

export function StatusBadge({ s }: { s: PostCloseItem['status'] }) {
  const v = STATUS[s] ?? { label: s, tone: 'bg-canvas' };
  return <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${v.tone}`}>{v.label}</span>;
}

/** 팀 승인 진행 한 줄 — 「회계팀 ✓ 김윤진 10-28 14:02 · 인차팀 대기」. */
export function ReviewChips({ it }: { it: PostCloseItem }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {it.reviews.map((r) => (
        <span key={r.team} className={`rounded border px-1.5 py-0.5 ${r.status === 'approved' ? 'border-emerald-300 bg-emerald-50' : r.status === 'rejected' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
              title={r.reason ?? undefined}>
          {r.team} {r.status === 'approved' ? `✓ ${r.byName ?? ''} ${r.at ?? ''}` : r.status === 'rejected' ? `✗ ${r.byName ?? ''} — ${r.reason ?? ''}` : '대기'}
        </span>
      ))}
    </div>
  );
}

/** 기안 한 장 — 목록·팝업 공용. `onDone` = 결정 뒤 다시 읽기. */
export function PostCloseCard({ it, onDone, compact = false }: { it: PostCloseItem; onDone: () => void; compact?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectTeam, setRejectTeam] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  async function decide(team: string, approve: boolean) {
    if (!approve && !reason.trim()) { setMsg('반려 사유를 적어 주세요.'); return; }
    if (approve && !window.confirm(`${team} 이름으로 승인합니다.${it.reviews.filter((r) => r.status !== 'approved').length <= 1 ? '\n마지막 승인이면 바로 버스인웹에 반영됩니다.' : ''}`)) return;
    setBusy(team); setMsg(null);
    try {
      const r = await api.decidePostClose({ draftId: it.draftId, team, approve, reason: approve ? undefined : reason.trim() });
      setMsg(r.status === 'applied' ? '모든 팀 승인 — 버스인웹에 반영했습니다.' : r.status === 'held' ? `반영 직전 점검에서 멈췄습니다 — ${r.message ?? ''}` : r.status === 'rejected' ? '반려했습니다.' : '승인했습니다 — 남은 팀을 기다립니다.');
      setRejectTeam(null); setReason('');
      pokePostClose();
      onDone();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const shown = compact ? it.lines.slice(0, 6) : it.lines;
  return (
    <div className="space-y-2 rounded-xl border border-line p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatusBadge s={it.status} />
        <span className="font-semibold">{it.draftNo ?? it.draftId}</span>
        <span className="rounded bg-canvas px-1.5 py-0.5 text-xs">{TYPE_LABEL[it.type ?? ''] ?? it.type ?? '—'}</span>
        <span className="rounded bg-canvas px-1.5 py-0.5 text-xs">{it.mon ?? '—'} 정산</span>
        <span className="ml-auto text-xs text-ink-faint">{it.drafterName ?? it.drafterEmail} · {it.createdAt}</span>
      </div>
      <ul className="space-y-0.5 rounded-lg bg-canvas px-3 py-2 text-sm">
        {shown.map((l, i) => <li key={i} className="break-words">· {l}</li>)}
        {shown.length < it.lines.length && <li className="text-ink-faint">… 외 {it.lines.length - shown.length}줄</li>}
      </ul>
      {(it.reason || it.prevent) && (
        <div className="text-sm">
          {it.reason && <div><span className="text-ink-soft">사유{it.reasonCode ? ` (${it.reasonCode})` : ''}:</span> {it.reason}</div>}
          {!compact && it.prevent && <div><span className="text-ink-soft">재발 방지:</span> {it.prevent}</div>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-soft">승인 팀</span>
        {it.reviews.length ? <ReviewChips it={it} /> : <span className="text-xs text-ink-faint">—</span>}
      </div>
      {it.message && it.status !== 'review' && <div className="text-sm text-ink-soft">{it.message}</div>}
      {!compact && it.result?.todo && it.result.todo.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
          <div className="font-semibold">회계 할 일</div>
          {it.result.todo.map((t, i) => <div key={i}>· {t}</div>)}
        </div>
      )}
      {!compact && it.result?.salary?.some((s) => !s.ok) && (
        <div className="text-xs text-ink-soft">명세: {it.result.salary.filter((s) => !s.ok).map((s) => `${s.target} ${s.note ?? ''}`).join(' · ')}</div>
      )}
      {it.canDecide.length > 0 && (
        <div className="space-y-2 border-t border-line pt-2">
          {it.canDecide.map((team) => (
            <div key={team} className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{team}</span>
              <button className="btn-primary" disabled={!!busy} onClick={() => void decide(team, true)}>{busy === team ? '처리 중…' : '승인'}</button>
              {rejectTeam === team ? (
                <>
                  <input className="field min-w-0 flex-1" placeholder="반려 사유(기안자에게 갑니다)" value={reason} maxLength={500}
                         onChange={(e) => setReason(e.target.value)} autoFocus />
                  <button className="btn" disabled={!!busy} onClick={() => void decide(team, false)}>반려</button>
                  <button className="btn-ghost" onClick={() => { setRejectTeam(null); setReason(''); }}>취소</button>
                </>
              ) : (
                <button className="btn-ghost" disabled={!!busy} onClick={() => setRejectTeam(team)}>반려…</button>
              )}
            </div>
          ))}
        </div>
      )}
      {msg && <div className="text-sm text-brand-700">{msg}</div>}
    </div>
  );
}

export default function PostClose() {
  const [params, setParams] = useSearchParams();
  const id = params.get('id') ?? '';
  const [view, setView] = useState<'pending' | 'all'>('pending');
  const [items, setItems] = useState<PostCloseItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.listPostCloseDrafts(id ? { id } : { view })
      .then((r) => setItems(r.items))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id, view]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="page-head">
        <h1 className="font-bold">마감 후 수정</h1>
        <p className="mt-1 text-sm text-ink-soft">
          DYOPS 「마감 후 수정」 기안이 올라오면 여기서 팀이 승인합니다 — 관리기사A·직영사업장 기사면 회계팀과 인차팀, 그 밖은 회계팀.
          필요한 팀이 모두 승인하면 버스인웹에 바로 반영됩니다(DYOPS 결재는 근거로 따로 남습니다).
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {id ? (
          <button className="btn-ghost" onClick={() => setParams({})}>← 목록으로</button>
        ) : (
          <div className="flex gap-1">
            <button className={view === 'pending' ? 'btn-primary' : 'btn'} onClick={() => setView('pending')}>승인 대기</button>
            <button className={view === 'all' ? 'btn-primary' : 'btn'} onClick={() => setView('all')}>전체(최근 200)</button>
          </div>
        )}
        <button className="btn-ghost ml-auto" onClick={load}>새로 고침</button>
      </div>
      {error && <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {items === null && !error && <div className="text-sm text-ink-soft">불러오는 중…</div>}
      {items?.length === 0 && <div className="text-sm text-ink-soft">{id ? '그 기안을 찾을 수 없습니다.' : view === 'pending' ? '승인 대기 중인 기안이 없습니다.' : '아직 올라온 기안이 없습니다.'}</div>}
      <div className="space-y-3">
        {items?.map((it) => <PostCloseCard key={it.draftId} it={it} onDone={load} />)}
      </div>
    </div>
  );
}
